import { FC, useEffect } from 'react';
import Form from '../../../../components/common/Forms/Form';
import { ClassInformationFields, normalizeInitialValues } from '../../../../config/tuitionMangerViewDetailsFields';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { fetchDropdownOptions } from '../../../../store/slices/Tuition/tuitionApplySlice';
 
interface ClassInformationProps {
  applicationData?: any;
}
 
const ClassInformation: FC<ClassInformationProps> = ({ applicationData }) => {
  const dispatch = useAppDispatch();
  const { dropdownOptions } = useAppSelector((state: any) => state.tuitionApply);
 
  useEffect(() => {
    if (!dropdownOptions || Object.keys(dropdownOptions).length === 0) {
      dispatch(fetchDropdownOptions({ fieldName: 'all', applType: 'AT001' }) as any);
    }
  }, [dispatch, dropdownOptions]);
 
  const courses = Array.isArray(applicationData?.courses) ? applicationData.courses : [];
 
  // Helper function to calculate total estimated fees for a specific course
  const calculateCourseTotalFees = (course: any) => {
    const feeFieldNames = [
      'estimatedTuitionFee',
      'estimatedBooksFee',
      'estimatedLabFee',
      'estimatedRegistrationFee',
      'estimatedOtherFee',
      'estimatedAirfareBaggageFee',
      'estimatedParkTollFee',
      'estimatedAutoRentalFee',
      'estimatedPublicTransportFee',
      'estimatedHotelFee',
      "estimatedMilesDollarValue"
    ];
 
    let totalFees = 0;
    feeFieldNames.forEach((fieldName) => {
      const value = Number(course[fieldName]) || 0;
      totalFees += value;
    });
 
    return totalFees.toFixed(2);
  };
 
  // Helper function to filter fields based on reimbursement type
  const getFilteredFields = (reimbursementType: any) => {
    const rt = reimbursementType;
 
    if (rt !== "CEU") {
      return ClassInformationFields.filter(
        (field) => field.fieldGroupType !== "travelRelated" && field.fieldGroupType !== "mileageRelated"
      );
    }
 
    return ClassInformationFields;
  };
 
  return (
    <div>
      <h2 className="h3">Class information</h2>
      {courses.length > 0 ? (
        courses.map((course: Partial<Record<string, any>> | undefined, index: number) => {
          const reimbursementTypeCode = course?.reimbursementType;
 
          const mappedReimbursementType = dropdownOptions?.reimbursement_type
            ? (dropdownOptions.reimbursement_type.find((item: any) => String(item.code) === String(reimbursementTypeCode))?.value || reimbursementTypeCode)
            : reimbursementTypeCode;
 
          const mappedOriginState = dropdownOptions?.state
            ? (dropdownOptions.state.find((item: any) => String(item.code) === String(course?.originState))?.value || course?.originState)
            : course?.originState;
 
          const mappedDestinationState = dropdownOptions?.state
            ? (dropdownOptions.state.find((item: any) => String(item.code) === String(course?.destinationState))?.value || course?.destinationState)
            : course?.destinationState;
 
          const isCeu = String(reimbursementTypeCode) === "1" || String(mappedReimbursementType) === "CEU";
 
          const mappedCourse = {
            ...course,
            reimbursementType: mappedReimbursementType,
            originState: mappedOriginState,
            destinationState: mappedDestinationState,
            estimatedMilesDollarValue: isCeu && course?.estimatedMilesDollarValue != null ? Number(course.estimatedMilesDollarValue).toFixed(2) : course?.estimatedMilesDollarValue,
            totalEstimatedCourseFees: calculateCourseTotalFees(course),
            classSchoolName: course?.schoolName || "",
            courseProgramType: course?.programType || "",
            originZipCode: course?.originZip || "",
            destinationZipCode: course?.destinationZip || "",
          };
 
          return (
            <div key={course?.id || course?.courseId || `course-${index}`}>
              <h3 className="h5 mt-6 mb-4">Class {index + 1}</h3>
              <Form
                fields={getFilteredFields(mappedCourse.reimbursementType)}
                initialValues={normalizeInitialValues(mappedCourse, getFilteredFields(mappedCourse.reimbursementType))}
                isViewDetails
                buttons={[]}
                className="mt-4"
              />
            </div>
          );
        })
      ) : (
        <p className="mt-4">No courses found</p>
      )}
    </div>
  );
};
 
export default ClassInformation;